/** Queue elapsed/ETA should follow this Vantage run, not the original submit time. */

export type ResearchQueueElapsedStatus = 'queued' | 'screening' | 'completed' | 'failed' | 'cancelled';

export function resolveResearchQueueTimerStart(item: {
  status?: ResearchQueueElapsedStatus | string;
  createdAt?: string | null;
  updatedAt?: string | null;
  runStartedAt?: string | null;
}): string | null {
  const status = String(item.status || '').toLowerCase();
  if (status === 'queued') {
    return null;
  }
  const runStarted = String(item.runStartedAt || '').trim();
  if (runStarted) return runStarted;
  if (status === 'screening') {
    return String(item.updatedAt || item.createdAt || '').trim() || null;
  }
  return String(item.createdAt || item.updatedAt || '').trim() || null;
}

export function estimateQueueEtaSeconds({
  createdAt,
  runStartedAt,
  progress,
  status,
  previousEtaSeconds,
  targetType,
}: {
  createdAt: string;
  runStartedAt?: string | null;
  progress: number;
  status: ResearchQueueElapsedStatus | string;
  previousEtaSeconds?: number;
  targetType?: 'company' | 'person' | 'unknown';
}): number | undefined {
  if (status === 'completed' || status === 'failed') return 0;

  const nowMs = Date.now();
  const elapsedAnchor = status === 'queued'
    ? null
    : (runStartedAt || createdAt);
  const startedAtMs = Date.parse(String(elapsedAnchor || ''));
  const elapsedSeconds = status === 'queued'
    ? 0
    : (Number.isFinite(startedAtMs) ? Math.max(1, Math.round((nowMs - startedAtMs) / 1000)) : 1);
  const normalizedProgress = Math.max(1, Math.min(99, Math.round(progress || 0)));

  const expectedTotalSeconds = targetType === 'company' ? 240 : 2700;
  const etaCapSeconds = targetType === 'company' ? 900 : 7200;

  const fallback = status === 'queued' ? expectedTotalSeconds : Math.max(60, expectedTotalSeconds - elapsedSeconds);
  const projectedTotal = elapsedSeconds > 0
    ? elapsedSeconds / (normalizedProgress / 100)
    : expectedTotalSeconds;
  let nextEta = Math.max(1, Math.min(etaCapSeconds, Math.round(projectedTotal - elapsedSeconds)));
  if (!Number.isFinite(nextEta) || nextEta <= 0) nextEta = fallback;

  const floorEta = Math.max(0, expectedTotalSeconds - elapsedSeconds);
  if (floorEta > nextEta) nextEta = floorEta;

  if (typeof previousEtaSeconds === 'number' && Number.isFinite(previousEtaSeconds) && previousEtaSeconds > 0) {
    const maxRise = targetType === 'person' ? 120 : 10;
    const maxDrop = targetType === 'person' ? 30 : 5;
    if (nextEta > previousEtaSeconds + maxRise) {
      nextEta = previousEtaSeconds + maxRise;
    }
    if (nextEta < previousEtaSeconds - maxDrop) {
      nextEta = previousEtaSeconds - maxDrop;
    }
  }

  return Math.max(1, nextEta);
}
